import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FC, ReactNode, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { InGameCurrency, Rarity, Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { Boost } from 'atoms/icons/Boost';
import { Cards } from 'atoms/icons/Cards';
import { Energy } from 'atoms/icons/Energy';
import { Shards } from 'atoms/icons/Shards';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM, HeadlineXL, LabelM, LabelS } from 'atoms/typography';
import {
  ANY_SPORT_MY_CARDS_ESSENCE,
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
  ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE,
  FOOTBALL_MARKET_COMMON_PACKS,
} from 'constants/__generated__/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { OnboardingHighlightName } from 'contexts/onboardingHighlight';
import { useSportContext } from 'contexts/sport';
import { useGetMyCardsBoostLink } from 'hooks/my-cards/useGetMyCardsBoostLink';
import { formatScarcity } from 'lib/cards';
import { glossary } from 'lib/glossary';
import { generateSportPath } from 'lib/routing/generateSportPath';
import { inGameCurrencyToRarity } from 'lib/scarcity';
import { laptopAndAbove } from 'style/mediaQuery';

import { GemIconFlat } from './assets/GemIconFlat';

export enum InGameCurrencyAgreggated {
  ENERGY = 'ENERGY',
  XP = 'XP',
  SHARDS = 'SHARDS',
  BLUEPRINTS = 'BLUEPRINT',
  GEMS = 'GEMS',
}

export type CurrencyData = {
  id: InGameCurrencyAgreggated;
  icon: ReactNode;
  amount?: number;
  details?: FC;
  visibleOnSports: Sport[];
  expanded?: boolean;
  onClick?: () => void;
  onboardingHighlightName?: OnboardingHighlightName;
};

type DetailsProps = { onClick?: () => void };

const SelectedCurrencyWrapper = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;
const RaritiesWrapper = styled(Horizontal)`
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: column;
    gap: 0;
  }
`;
const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit);
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;

export const useUserCurrencies = (
  expandedCurrencies?: InGameCurrencyAgreggated[]
): CurrencyData[] => {
  const navigate = useNavigate();
  const { sport } = useSportContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { currentUser } = useCurrentUserContext();
  const [seeAllEnergy, setSeeAllEnergy] = useState(false);

  const { energyBalance, xpBalances, shardsChests, anyUserSportProfile } =
    currentUser || {};

  const { blueprintCardsCount = 0 } = anyUserSportProfile || {};

  const filteredXpBalances =
    currentUser?.betaTesterFootballCommonCardsGame || sport !== Sport.FOOTBALL
      ? xpBalances
      : xpBalances?.filter(
          balance => balance.currency !== InGameCurrency.COMMON_XP
        );
  const totalEnergyBalance = energyBalance?.reduce(
    (sum, { currency, amount }) => {
      const rarity = inGameCurrencyToRarity[currency];
      if (!rarity) return sum;
      // do not show energy balance if user has no card of this rarity
      if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
        return sum;
      }
      return sum + amount;
    },
    0
  );
  const totalShards = [
    ...(shardsChests || []),
    { cardShardsCount: currentUser?.commonCardPoints || 0 },
  ]?.reduce((acc, chest) => acc + chest.cardShardsCount, 0);
  const firstNonZeroBalance = (filteredXpBalances || []).find(
    ({ amount }) => amount > 0
  );
  const firstNonZeroBalanceRarity = firstNonZeroBalance?.currency
    ? inGameCurrencyToRarity[firstNonZeroBalance.currency]
    : undefined;
  const totalXPBalance = filteredXpBalances?.reduce(
    (sum, { amount }) => sum + amount,
    0
  );
  const gemsCount = currentUser?.gemsBalances[0]?.amount || 0;

  return [
    {
      id: InGameCurrencyAgreggated.SHARDS,
      icon: <Shards size={2} color="var(--c-rivals-win)" />,
      amount: totalShards,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Shards size={2} color="var(--c-rivals-win)" />
            <LabelM brand color="var(--c-rivals-win)">
              <FormattedMessage
                id="useUserCurrencies.essence"
                defaultMessage="Essence"
              />
            </LabelM>
            <HeadlineXL brand>
              <FormattedNumber value={totalShards || 0} />
            </HeadlineXL>
          </Vertical>
          <RaritiesWrapper>
            {currentUser?.betaTesterFootballCommonCardsGame && (
              <RarityWrapper
                as={Link}
                to={FOOTBALL_MARKET_COMMON_PACKS}
                onClick={onClick}
              >
                <LabelS>{formatScarcity(Rarity.common)}</LabelS>
                <BodyM brand>
                  <FormattedNumber value={currentUser?.commonCardPoints} />
                </BodyM>
              </RarityWrapper>
            )}
            {shardsChests?.map(({ cardShardsCount, rarity }) => {
              return (
                <RarityWrapper
                  key={rarity}
                  as={Link}
                  to={generateSportPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
                    params: { type: rarity },
                    sport,
                  })}
                  onClick={onClick}
                >
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <BodyM brand>
                    <FormattedNumber value={cardShardsCount} />
                  </BodyM>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.SHARDS),
      onClick: () =>
        navigate(generateSportPath(ANY_SPORT_MY_CARDS_ESSENCE, { sport })),
      onboardingHighlightName: 'CommonEssenceBalance' as const,
    },
    {
      id: InGameCurrencyAgreggated.XP,
      icon: <Boost size={2} color="var(--c-rivals-friendlies)" />,
      amount: totalXPBalance,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Boost size={2} color="var(--c-rivals-friendlies)" />
            <LabelM brand color="var(--c-rivals-friendlies)">
              <FormattedMessage
                id="XpBalances.xpReserve"
                defaultMessage="XP Reserve"
              />
            </LabelM>
            <HeadlineXL brand>
              <FormattedNumber value={totalXPBalance || 0} />
            </HeadlineXL>
          </Vertical>
          <RaritiesWrapper>
            {filteredXpBalances?.map(
              ({ currency, amount, capAlmostReached }) => {
                const rarity = inGameCurrencyToRarity[currency];
                if (!rarity) return null;

                return (
                  <RarityWrapper
                    key={currency}
                    as={Link}
                    to={getMyCardsBoostLink({
                      rarity,
                      sport,
                    })}
                    onClick={onClick}
                  >
                    <LabelS>{formatScarcity(rarity)}</LabelS>
                    <Horizontal>
                      <BodyM brand>
                        <FormattedNumber value={amount} />
                      </BodyM>
                      {capAlmostReached && (
                        <FontAwesomeIcon
                          size="xs"
                          color="var(--c-yellow-400)"
                          icon={faTriangleExclamation}
                        />
                      )}
                    </Horizontal>
                  </RarityWrapper>
                );
              }
            )}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.XP),
      onClick: () => {
        if (firstNonZeroBalanceRarity) {
          navigate(
            getMyCardsBoostLink({
              rarity: firstNonZeroBalanceRarity,
              sport,
            })
          );
        } else {
          navigate(FOOTBALL_MARKET_COMMON_PACKS);
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.ENERGY,
      icon: <Energy size={2} color="var(--c-yellow-300)" />,
      amount: totalEnergyBalance,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Energy size={2} color="var(--c-yellow-300)" />
            <LabelM brand color="var(--c-yellow-300)">
              <FormattedMessage
                id="useUserCurrencies.energy"
                defaultMessage="Energy"
              />
            </LabelM>
            <HeadlineXL brand>
              <FormattedNumber value={totalEnergyBalance || 0} />
            </HeadlineXL>
          </Vertical>
          <RaritiesWrapper gap={0}>
            {energyBalance?.map(({ currency, amount, capAlmostReached }) => {
              const rarity = inGameCurrencyToRarity[currency];
              if (!rarity) return null;
              // do not show energy balance if user has no card of this rarity
              if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
                return null;
              }

              return (
                <RarityWrapper key={currency}>
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <Horizontal>
                    <BodyM brand>
                      <FormattedNumber value={amount} />
                    </BodyM>
                    {capAlmostReached && (
                      <FontAwesomeIcon
                        size="xs"
                        color="var(--c-yellow-400)"
                        icon={faTriangleExclamation}
                      />
                    )}
                  </Horizontal>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
          <Button
            size="compact"
            fullWidth
            color="quaternary"
            disableDebounce
            onClick={() => setSeeAllEnergy(prev => !prev)}
          >
            <FormattedMessage
              {...(seeAllEnergy ? glossary.showLess : glossary.showMore)}
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.ENERGY),
    },
    {
      id: InGameCurrencyAgreggated.BLUEPRINTS,
      icon: <Cards color="var(--c-rivals-purple-600)" />,
      amount: blueprintCardsCount,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Cards size={2} color="var(--c-rivals-purple-600)" />
            <LabelM brand color="var(--c-rivals-purple-600)">
              <FormattedMessage
                id="useUserCurrencies.cardsToReveal"
                defaultMessage="Cards to reveal"
              />
            </LabelM>
            <HeadlineXL brand>
              <FormattedNumber value={blueprintCardsCount || 0} />
            </HeadlineXL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            to={generateSportPath(
              ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE,
              {
                sport,
                params: {
                  type: Rarity.limited,
                },
              }
            )}
          >
            <FormattedMessage
              id="useUserCurrencies.goToReveal"
              defaultMessage="Go to Reveal"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: () =>
        navigate(
          generateSportPath(ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE, {
            sport,
            params: {
              type: Rarity.limited,
            },
          })
        ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(
        InGameCurrencyAgreggated.BLUEPRINTS
      ),
    },
    !!currentUser?.betaTesterFootballCommonCardsGame && {
      id: InGameCurrencyAgreggated.GEMS,
      icon: <GemIconFlat size={2} />,
      amount: gemsCount,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Cards size={2} color="var(--c-rivals-win)" />
            <LabelM brand color="var(--c-rivals-win)">
              <FormattedMessage
                id="useUserCurrencies.gems"
                defaultMessage="Gems"
              />
            </LabelM>
            <HeadlineXL brand>
              <FormattedNumber value={gemsCount || 0} />
            </HeadlineXL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            to={`${FOOTBALL_MARKET_COMMON_PACKS}?buy_gems=true`}
          >
            <FormattedMessage
              id="useUserCurrencies.getGems"
              defaultMessage="Get Gems"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: () => navigate(`${FOOTBALL_MARKET_COMMON_PACKS}?buy_gems=true`),
      visibleOnSports: [Sport.FOOTBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.GEMS),
    },
  ].filter(Boolean);
};
